<template>
  <FormulateForm v-model="filterForm" class="flex space-x-4" @submit="onSubmit">
    <div class="w-full flex flex-col">
      <!-- Trigger/button row -->
      <div class="w-full flex items-center justify-between flex-wrap gap-2">
        <slot name="trigger">
          <button
            type="button"
            class="p-3 btn btn-trans-black mb-0"
            @click="setShowModal(true)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4 mr-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
              />
            </svg>
            Filters
          </button>
        </slot>

        <FilterFormKeywords :context="context" @on-submit="onSubmit" />
      </div>

      <!-- Modal -->
      <div
        class="fixed z-70 inset-0 overflow-y-auto"
        :class="
          showModal
            ? 'transition-all duration-300 ease-in-out'
            : 'h-0 opacity-0 pointer-events-none'
        "
      >
        <div
          class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          ></div>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span
            class="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
            >&#8203;</span
          >
          <div
            class="transform delay-100 inline-block rounded-lg sm:mt-8 sm:mb-20 text-left shadow transition-all align-bottom sm:align-middle overflow-visible relative w-full lg:min-w-2xl sm:max-w-3xl sm:w-full"
          >
            <div
              class="bg-white rounded-xl shadow-xl border border-truegray-300 p-10 z-70 text-black w-full"
            >
              <div
                class="flex justify-between items-center pb-4 w-full border-b border-truegray-300"
                :class="{ 'mb-4': !selectedFiltersCount }"
              >
                <div class="flex flex-row items-center">
                  <h3 class="mb-0">Filters</h3>
                  <span class="text-xs ml-4"
                    >{{ selectedFiltersCount }} selected
                  </span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-truegray-500 hover:text-primary cursor-pointer transform hover:rotate-180 transition-all duration-300 ease-in-out"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  @click="setShowModal(!showModal)"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div v-if="selectedFiltersCount" class="flex flex-col my-4">
                <span class="text-xs"> Active Filters </span>
                <div
                  class="flex flex-wrap items-center pb-4 w-full border-b border-truegray-300 mt-3 gap-1"
                >
                  <div
                    v-for="(option, key) in selectedFilters"
                    :key="key"
                    data-list="selectList"
                  >
                    <button
                      type="button"
                      class="float-left rounded-full w-auto text-sm relative outline-none focus:outline-none border-0 flex flex-grow-0 justify-start items-stretch text-gray-500"
                      :class="{ 'cursor-default': !canRemoveFilter(option) }"
                      @click="() => removeFilter(option)"
                    >
                      <span
                        :title="getFilterTitle(option)"
                        class="text-white py px-2 text-xs py-1 bg-primary rounded-l-full"
                      >
                        {{ option.data.text }}
                      </span>
                      <div
                        v-if="canRemoveFilter(option)"
                        :style="{ marginLeft: '1px' }"
                        class="text-white flex items-center justify-center rounded-r-full px-1 bg-primary"
                      >
                        <svg
                          :style="{ right: '1px' }"
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-3 w-3 relative"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Filters -->
              <div
                v-show="showInput('dates')"
                class="grid grid-cols-2 gap-2 pb-6 mb-6 border-b border-truegray-300"
              >
                <div class="text-md col-span-2">Engagement date</div>

                <div v-if="showInputDateYear" class="col-span-1">
                  <FormulateInput
                    type="select"
                    name="dateYear"
                    label="Year:"
                    :options="dateYearsOptions"
                  />
                </div>
                <template v-else>
                  <FormulateInput
                    type="date"
                    name="dateFrom"
                    label="From:"
                    :max="maxDateFrom"
                  />
                  <FormulateInput
                    type="date"
                    name="dateTo"
                    label="To:"
                    :min="filterForm.dateFrom"
                  />
                </template>
                <a
                  v-if="canToggleDateSelector"
                  class="text-xs col-span-2 mt-2"
                  @click="toggleDateSelector"
                >
                  {{ dateSelectorToggleText }}
                </a>
              </div>

              <div class="text-md col-span-2">Tags</div>
              <div
                v-show="showInput('industries')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="industries"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['industries']"
                  :options="tagsIndustries"
                  :count="filterForm.industries?.length || 0"
                  :is-collapsed="isTagsCollapsed['industries']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('schoolSupport')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="schoolSupport"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['schoolSupport']"
                  :options="tagsSchoolSupport"
                  :count="filterForm.schoolSupport?.length || 0"
                  :is-collapsed="isTagsCollapsed['schoolSupport']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('preferences')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="preferences"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['preferences']"
                  :options="tagsPreferences"
                  :count="filterForm.preferences?.length || 0"
                  :is-collapsed="isTagsCollapsed['preferences']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('subjects')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="subjects"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['subjects']"
                  :options="tagsSubjects"
                  :count="filterForm.subjects?.length || 0"
                  :is-collapsed="isTagsCollapsed['subjects']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('engagementTypes')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="engagementTypes"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['engagementTypes']"
                  :options="tagsPreferences"
                  :count="filterForm.engagementTypes?.length || 0"
                  :is-collapsed="isTagsCollapsed['engagementTypes']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('organisationsSupport')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="organisationsSupport"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['organisationsSupport']"
                  :options="tagsOrganisations"
                  :count="filterForm.organisationsSupport?.length || 0"
                  :is-collapsed="isTagsCollapsed['organisationsSupport']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('softSkills')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="softSkills"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['softSkills']"
                  :options="tagsSoftSkills"
                  :count="filterForm.softSkills?.length || 0"
                  :is-collapsed="isTagsCollapsed['softSkills']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('techSkills')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="techSkills"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['techSkills']"
                  :options="tagsTechSkills"
                  :count="filterForm.techSkills?.length || 0"
                  :is-collapsed="isTagsCollapsed['techSkills']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('years')"
                class="flex flex-col space-y-2 w-full mt-4"
              >
                <FormulateInput
                  name="years"
                  type="tags"
                  :label="FILTER_FORM_LABEL_MAP['years']"
                  :options="tagsYears"
                  :count="filterForm.years?.length || 0"
                  :is-collapsed="isTagsCollapsed['years']"
                  :is-filter="true"
                />
              </div>

              <div
                v-show="showInput('policeChecked')"
                class="flex justify-start items-end pb-[20px] pr-2 w-full mt-3"
              >
                <FormulateInput
                  name="policeChecked"
                  type="toggle"
                  :val="filterForm.policeChecked"
                  :label="FILTER_FORM_LABEL_MAP.policeChecked"
                />
              </div>

              <div
                class="flex justify-between items-center mt-6 pt-6 border-t w-full"
              >
                <p class="text-red-500 cursor-pointer" @click="clearFilters">
                  Clear Filters
                </p>
                <FormulateInput
                  type="submit"
                  :disabled="submitDisabled"
                  label="Apply filters"
                  :wrapper-class="['mb-0']"
                  :input-class="[
                    'mb-0',
                    {
                      'cursor-not-allowed opacity-50': submitDisabled,
                    },
                  ]"
                  :title="
                    filtersSubmitted ? 'Please update filters to reapply' : ''
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FormulateForm>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted } from 'vue'
import { useStore } from '../../../../../js/store/index.js'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import {
  EVENT_KEYS,
  FILTER_FORM_LABEL_MAP,
} from '../../../../../js/util/constants.js'
import FilterFormKeywords from '../FilterFormKeywords.vue'

export default defineComponent({
  name: 'ModalFilter',

  components: {
    FilterFormKeywords,
  },

  props: {
    filters: {
      type: Array,

      default: () => [
        'industries',
        'schoolSupport',
        'preferences',
        'policeChecked',
      ],
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    context: {
      type: String,
      default: 'connections',
    },
  },

  emits: ['on-submit'],

  setup(props, { emit }) {
    const store = useStore()

    const showModal = ref(false)
    const filterForm = ref(store.getters['connections/getFilterForm'])
    const filtersSubmitted = ref(false)

    const submitDisabled = computed(() => {
      return props.isLoading || filtersSubmitted.value
    })

    const tagsIndustries = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsIndustries',
      ),
    )

    const tagsSchoolSupport = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsSchoolSupport',
      ),
    )

    const tagsPreferences = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsPreferences',
      ),
    )

    const tagsOrganisations = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsOrganisations',
      ),
    )

    const tagsSubjects = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsSubjects',
      ),
    )

    const tagsYears = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsYears',
      ),
    )

    const tagsSoftSkills = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsSoftSkills',
      ),
    )

    const tagsTechSkills = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsTechSkills',
      ),
    )

    const isTagsCollapsed = ref({
      industries: true,
      schoolSupport: true,
      preferences: true,
      subjects: true,
      engagementTypes: true,
      organisationsSupport: true,
      softSkills: true,
      techSkills: true,
      years: true,
    })

    const selectedFilters = computed(
      () => store.getters['connections/getSelectedFilters'],
    )

    const selectedFiltersCount = computed(
      () => store.getters['connections/getSelectedFiltersCount'],
    )

    const filtersPayload = computed(
      () => store.getters['connections/getFilterFormPayload'],
    )

    const maxDateFrom = computed(() => {
      const dateTo = filterForm.value.dateTo

      if (dateTo) {
        return dateTo
      }

      return null
    })

    const dateYearsOptions = computed(
      () => store.getters['connections/getReportsYearsOptions'],
    )

    const DATE_SELECTOR_VALUES = ['dateYear', 'dateRange']
    const DATE_SELECTOR_DEFAULT = DATE_SELECTOR_VALUES[0]
    const selectedDateSelector = ref(DATE_SELECTOR_DEFAULT)

    const showInputDateYear = computed(() => {
      return (
        // 'dateYear' input is selected AND parent component says we can show it!
        selectedDateSelector.value === DATE_SELECTOR_DEFAULT &&
        !!props.filters.find((f) => f === selectedDateSelector.value)
      )
    })

    const dateSelectorToggleText = computed(() => {
      if (selectedDateSelector.value === DATE_SELECTOR_DEFAULT) {
        return 'Clear selection / choose a date range'
      }

      return 'Choose a year'
    })

    const canToggleDateSelector = computed(() => {
      return DATE_SELECTOR_VALUES.every((val) => showInput(val))
    })

    const toggleDateSelector = () => {
      const index = 1 - DATE_SELECTOR_VALUES.indexOf(selectedDateSelector.value)
      selectedDateSelector.value = DATE_SELECTOR_VALUES[index]
    }

    const canRemoveFilter = (option) => {
      return option.name !== 'dateYear'
    }

    const showInput = (filter) => {
      return props.filters.find((f) => f === filter)
    }

    const initIsTagsCollapsed = () => {
      for (const key of Object.keys(isTagsCollapsed.value)) {
        isTagsCollapsed.value[key] = true
      }
    }

    const setShowModal = (val) => {
      EventBus.$emit('modal_state_changing', val)
      showModal.value = val
      initIsTagsCollapsed()
    }

    const getFilterTitle = (opt) => {
      return store.getters['connections/getFilterTitle'](opt)
    }

    const removeFilter = (opt) => {
      store.dispatch('connections/dispatchRemoveFilter', opt)
    }

    const clearFilters = () => {
      store.dispatch('connections/dispatchClearFilters')
    }

    const onSubmit = () => {
      filtersSubmitted.value = true
      setShowModal(false)
      emit('on-submit', filtersPayload.value)
    }

    watch(filterForm, () => {
      store.dispatch('connections/dispatchUpdateFilterForm', filterForm.value)
      filtersSubmitted.value = false
    })

    watch(canToggleDateSelector, () => {
      // Reset the chosen date selection type!
      selectedDateSelector.value = DATE_SELECTOR_DEFAULT
      if (canToggleDateSelector.value) {
        filterForm.value.dateYear = dateYearsOptions.value?.[0] || null
      }
    })

    onMounted(async () => {
      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_DATE_YEAR_UPDATED,
        () => {
          if (canToggleDateSelector.value) {
            toggleDateSelector()
          }
        },
      )

      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_DATE_UPDATED,
        (key) => {
          filterForm.value[key] = null
        },
      )

      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_KEYWORDS_UPDATED,
        (value) => {
          filterForm.value.keywords = value
        },
      )

      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_TAGS_TOGGLE_COLLAPSED,
        (type) => {
          const value = isTagsCollapsed.value[type]
          isTagsCollapsed.value[type] = !value
        },
      )
    })

    return {
      FILTER_FORM_LABEL_MAP,
      filterForm,
      showModal,
      filtersSubmitted,
      submitDisabled,
      tagsIndustries,
      tagsSchoolSupport,
      tagsPreferences,
      tagsSubjects,
      tagsOrganisations,
      tagsYears,
      tagsSoftSkills,
      tagsTechSkills,
      isTagsCollapsed,
      selectedFilters,
      selectedFiltersCount,
      maxDateFrom,
      dateYearsOptions,
      dateSelectorToggleText,
      toggleDateSelector,
      canRemoveFilter,
      canToggleDateSelector,
      showInputDateYear,
      showInput,
      setShowModal,
      getFilterTitle,
      removeFilter,
      clearFilters,
      onSubmit,
    }
  },
})
</script>
